/* System defined core library */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';

/* User defined library/components */
import { BootstrapParams } from '../../../../bootstrap-params';
import { LoaderService } from '../../../shared/services/loader.service';
import { RoutingService } from '../../../shared/services/routing.service';
import { TimerService } from '../../../shared/services/timer.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { MetricApiService } from '../../shared/services/metric-api.service';
import { PaymentApiService } from '../../shared/services/payment-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { CardDetail } from '../../../shared/models/scheduling/carddetail.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { CreatePaymentSessionRequest, CreatePaymentSessionResponse } from '../../../shared/models/scheduling/paymentSession.model';
import { Session } from '../../../shared/models/scheduling/session.model';
import { Site } from '../../../shared/models/scheduling/site.model';

/* User defined enums/constants */
import { CardFee, ComponentType, Fee, LogLevel, NavigationAction } from '../../../shared/enums/scheduling.enum';
import { SchedulingErrors } from '../../../shared/constants/scheduling.constant';


@Component({
  selector: 'app-payment-cancellation',
  templateUrl: './payment-cancellation.component.html'
})
export class PaymentCancellationComponent implements OnInit {

  @HostListener('window:popstate', ['$event'])
  onPopState(event: any) {
    this.selectedExamDetailService.cancellationFee = {
      totalAmount: 0,
      currency: null
    };
    this.selectedExamDetailService.postedErrorMessage = '';

    this.metricApiService.logNavigation('Payment', NavigationAction.backwardsNavigation);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: any) {
    if (this.isBookingInProgress) {
      let confNum = '';
      if (this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length > 1) {
        confNum = this.selectedAppointments[0].exam.sessions[0].confirmationNumber;
      } else {
        confNum = this.selectedAppointments[0].ConfirmationNumber;
      }

      this.metricApiService.log(LogLevel.Debug, `Candidate unloaded the Payment page while booking was in progress.\nConfirmationNumber: ${confNum}`);
    }
  }

  /* User defined private/public variables  */
  private cancelFailed: boolean;
  public booking: Booking;
  public cancelledConfirmationList: string[] = [];
  public errorMessage: any;
  public isBookingInProgress: boolean = false;
  public selectedAppointments: AppointmentDetail[] = [];
  public postedErrorMessage: string = '';
  public cardDetail = new CardDetail();
  public paymentOrchestratorSessionDetail: CreatePaymentSessionResponse;
  public totalAmount: number = 0.00;
  public currencyCode: string;
  public selectedSite: Site;
  public voucherPayment: Payment[];

  /* Service subscriptions */
  booking$: Subscription;
  paymentOrchestratorSessionDetail$: Subscription;
  selectedAppointments$: Subscription;
  translateLanguage$: Subscription;
  voucherPayment$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private bootstrapParams: BootstrapParams,
    private metricApiService: MetricApiService,
    private paymentApiService: PaymentApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private timerService: TimerService,
    private translate: TranslateService,
    private utilityService: UtilityService,
    private loaderService: LoaderService
  ) {
    this.booking$ = selectedExamDetailService.booking.subscribe(
      booking => {
        this.booking = booking;
      });

    this.paymentOrchestratorSessionDetail$ = selectedExamDetailService.paymentOrchestratorSessionDetail.subscribe(
      paymentOrchestratorSessionDetail => {
        this.paymentOrchestratorSessionDetail = paymentOrchestratorSessionDetail;
      });

    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.voucherPayment$ = selectedExamDetailService.voucherPayment.subscribe(
      payment => {
        this.voucherPayment = payment;
      });
  }

  /* Initialize the component & call required methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.PaymentCancellationComponent)) return;
    this.totalAmount = this.selectedExamDetailService.cancellationFee['totalAmount'];
    this.currencyCode = this.selectedExamDetailService.cancellationFee['currency'];
    this.selectedSite = this.selectedAppointments[0].appointmentSlot.site;
    this.isBookingInProgress = false;
  }

  // go back when click previous button
  goBack(): void {
    this.routingService.backwardNavigation(ComponentType.PaymentCancellationComponent);
  }

  // final submit to cancel an appointment
  submit(event: any): void {
    this.isBookingInProgress = true;

    // Sort selected Appoinement list for authorization and Settlement process
    // Both are taking max confirmation number for settlement payment and authorize
    this.selectedAppointments.sort((leftSide, rightSide): number => {
      if (leftSide.ConfirmationNumber < rightSide.ConfirmationNumber) { return -1; }
      if (leftSide.ConfirmationNumber > rightSide.ConfirmationNumber) { return 1; }
      return 0;
    });

    this.redirectToPayment();
  }

  redirectToPayment(): void {
    this.loaderService.showActive();

    let paymentDetails: CreatePaymentSessionRequest = new CreatePaymentSessionRequest(
      this.totalAmount,
      this.currencyCode,
      this.booking.client.code,
      this.selectedAppointments[this.selectedAppointments.length - 1].program.code,
      this.selectedAppointments[this.selectedAppointments.length - 1].exam.code,
      CardFee.Cancel,
      this.selectedExamDetailService.isProctored,
      !!this.selectedExamDetailService.terminationUrl ? this.selectedExamDetailService.terminationUrl : undefined
    );

    let examDetails: any[] = [];
    this.selectedAppointments.forEach((appointment: AppointmentDetail) => {
      examDetails.push({
        "examId": appointment.exam.id,
        "amount": appointment.payments.filter((appt) => {
          return appt.transactionType === Fee.CancelFee;
        })[0].amount
      });
    });

    this.selectedExamDetailService.postedConfirmationNumber = "";
    let confirmationNumbers: string[] = [];
    let appointmentIds: string[] = [];
    let examDetailsForSettlement: any[] = [];
    let examAccommodationsForNativeDelete: any[] = [];

    this.selectedAppointments.forEach((appointment: AppointmentDetail) => {
      // Create selected exam details Request for Settlement process in cancel flow
      let examFee: any = appointment.payments.filter(payment => payment.transactionType === Fee.CancelFee);
      //appointmentIds.push(appointment.id);
      examAccommodationsForNativeDelete.push({
        'id': appointment.exam.id,
        'testingAccommodations': appointment.exam.testingAccommodations
      });

      if (appointment.exam.sessions && appointment.exam.sessions.length) {
        appointment.exam.sessions.forEach((session: Session) => {
          confirmationNumbers.push(session.confirmationNumber);
          appointmentIds.push(session.appointmentId);
          examDetailsForSettlement.push(
            {
              "confirmationNumber": session.confirmationNumber,
              "amount": examFee[0].amount
            }
          );
        })
      } else {
        confirmationNumbers.push(appointment.ConfirmationNumber);
        appointmentIds.push(appointment.id);
        examDetailsForSettlement.push(
          {
            "confirmationNumber": appointment.ConfirmationNumber,
            "amount": examFee[0].amount
          });
      }
    });

    this.schedulingApiService.prepareBookingCancel(
      paymentDetails
    )
      .subscribe((prepResponse) => {
        if ((!!prepResponse && prepResponse.status) && (!!this.paymentOrchestratorSessionDetail && !!this.paymentOrchestratorSessionDetail.paymentOrchestratorUrl)) {
          this.selectedExamDetailService.setExamDetailsForSettlement(examDetails);
          this.selectedExamDetailService.setAppointmentIds(appointmentIds);
          this.selectedExamDetailService.setExamAccommodationsForNativeDelete(examAccommodationsForNativeDelete);
          this.selectedExamDetailService.ssoToken = this.bootstrapParams.Token;
          this.metricApiService.log(LogLevel.Info, 'Attempting to set session data in local storage.');
          let storageErr = this.selectedExamDetailService.saveToLocalStorage(this.bootstrapParams.sessionGuid);

          this.isBookingInProgress = false;

          if (!storageErr) {
            this.metricApiService.log(LogLevel.Info, `Successfully set local storage session data.`);
            this.routingService.navigateToUrl(this.paymentOrchestratorSessionDetail.paymentOrchestratorUrl);
          } else if (!!storageErr && !!storageErr.message.id) {
            this.metricApiService.log(LogLevel.Debug, `Error setting session data in local storage: ${JSON.stringify(storageErr.error)}`);
            this.errorMessage = storageErr.message;
            this.isBookingInProgress = false;
            this.loaderService.hideActive();
          }
        } else if (!!prepResponse.errorId) {
          this.errorMessage = {
            id: prepResponse.errorId,
            message: 'errors.error',
            description: prepResponse.message
          };
          this.isBookingInProgress = false;
          this.loaderService.hideActive();
        }
      }, (err) => {
        this.errorMessage = {
          id: SchedulingErrors.Default,
          message: 'errors.error',
          description: 'errors.thereWasAnIssue'
        };
        this.isBookingInProgress = false;
        this.loaderService.hideActive();
      });
  }

  /* Navigate away from proscheduler(to term/prometric site) */
  goToPrometric(): void {
    this.routingService.navigateAwayFromProscheduler();
  }
}
